/**
 * KB-3425 — why pro's auth does not use IndexedDB, and why there is no timeout
 * anywhere in this decision.
 *
 * ## The failure
 *
 * Firebase 12.16.0, `@firebase/auth/dist/esm/index-d90d2ee5.js`.
 * `IndexedDBLocalPersistence._isAvailable()` (`:8348`) opens the database via
 * `_openDatabase()` (`:8174`), which listens for `error`, `upgradeneeded` and
 * `success` — no `blocked` listener, no timeout. KB-3386 measured a store on
 * the sibling origin firing NONE of those three in 8001 ms. The `try/catch`
 * around it catches a REJECTION; a pending promise is not a rejection, so
 * `_isAvailable()` returns neither `true` nor `false`. It returns nothing, ever.
 *
 * `PersistenceUserManager.create` (`:2130`) filters the hierarchy with
 * `Promise.all(...)` over `_isAvailable()`, which cannot settle while any member
 * is pending. `auth._initializationPromise` then never settles, so
 * `onAuthStateChanged` never fires its first callback and the app sits behind a
 * gate it can never leave, with no console error.
 *
 * ## ⛔ Three fixes were written for this. All three reintroduced the defect.
 *
 * Recorded in full because the pattern is the lesson, not any one bug:
 *
 * | Attempt | Shape | How it failed |
 * |---|---|---|
 * | `main` | `getAuth(app)`, then `setPersistence(browserLocal)` in AuthContext | the original wedge |
 * | 1 | hierarchy without IndexedDB, `setPersistence` up to it after construction | `setPersistence` (`:2116`) moves the session INTO IndexedDB and DELETES it from localStorage; the next boot searches only the hierarchy it was handed (`:2151`), which lacked IndexedDB — signed every user out on every reload |
 * | 2 | keep IndexedDB in the hierarchy, wrap it, bound its four async methods | a merely-SLOW store passes `_isAvailable` but misses the bound on `_get`; the bounded `null` is indistinguishable from a real "no user", and the SDK acts on it DESTRUCTIVELY — `initializeCurrentUser` (`:2782`) → `directlySetCurrentUser(null)` (`:3139`) → `removeCurrentUser()` → a real `_remove`. The session is not stalled, it is gone, and a reload cannot recover it |
 *
 * Attempt 2 also made things worse for everyone rather than only for the
 * wedged: `create` ends by unconditionally removing the key from every
 * NON-selected persistence (`:2193-2200`), so every boot with IndexedDB
 * available migrates the session OUT of localStorage and INTO the store this
 * ticket was filed about.
 *
 * ## The constraint the three failures share
 *
 * **A bound must never produce a value its caller can mistake for ground
 * truth.** Every attempt handed the SDK a timeout dressed as an answer —
 * `false` for "unavailable", `null` for "no user", a resolved promise for "the
 * write landed" — and the SDK is entitled to act on all three, destructively.
 *
 * That is the user-facing honesty rule this ticket is about (AC-5: never render
 * absence you did not verify) applied one layer down, to an API caller instead
 * of a person.
 *
 * ## So: the SDK is never handed IndexedDB
 *
 * `src/services/firebaseService.ts` constructs auth on
 * `[browserLocalPersistence, browserSessionPersistence, inMemoryPersistence]`.
 * `BrowserLocalPersistence` (`:7428`) and `BrowserSessionPersistence` (`:7748`)
 * both extend `BrowserPersistenceClass`, whose `_isAvailable` (`:7378`) is a
 * synchronous `storage` read in a `try/catch` — no request, nothing to hang on.
 * `inMemoryPersistence` has no storage at all. IndexedDB is not in the list, so
 * `create` never calls `_isAvailable` or `_get` on it, and persistence selection
 * cannot hang on it.
 *
 * **There is no bound because there is nothing to bound.** That is the point: a
 * deadline is a source of false answers, and the only way to satisfy the
 * constraint above with certainty is to not need one.
 *
 * ⚠️ That closes the persistence hierarchy only. Auth requests also await two
 * IndexedDB opens outside it — the heartbeat header and the App Check token —
 * neither in code pro owns. The heartbeat door is bounded at the open itself,
 * where a deadline's only product is the SDK's own "storage unavailable" path.
 * The App Check store is not: its "unavailable" reads as "no cached token", a
 * false answer that can end in a sign-out. `indexedDbOpenBound.ts` (KB-3467).
 *
 * ### What this costs, stated honestly
 *
 * Any user whose session lives ONLY in IndexedDB is signed out once. On `main`
 * every successful boot ends with `await setPersistence(auth,
 * browserLocalPersistence)` (`AuthContext.tsx:392` on `origin/main`, verified),
 * and `setPersistence` writes the user to the new persistence and removes it
 * from the old (`:2116-2126`) — so the last write of every boot puts the
 * session in `localStorage`, and that is where it should be at rest.
 *
 * ⚠️ **That is an argument from code paths, not a measurement of the
 * population.** There is no telemetry on where users' session blobs actually
 * live, and the one browser that could have been inspected had its storage
 * cleared to confirm AC-1. The residual cohort is whoever's last boot did not
 * reach that line: someone already wedged (locked out regardless, and the
 * population this ticket exists for), or someone who closed the tab inside the
 * window between `create()` and it.
 *
 * ### What is lost
 *
 * IndexedDB's cross-tab sync path (`_poll` + service worker). Cross-tab sync
 * itself is NOT lost: `browserLocalPersistence._addListener` (`:7534`) attaches
 * a `storage` event listener, with a polling fallback for engines that drop the
 * event in background tabs. A different mechanism, not an absent one.
 */

/**
 * The database Firebase Auth uses for IndexedDB persistence — hardcoded in the
 * SDK as `DB_NAME`.
 *
 * Still exported, and still relevant even though auth no longer uses it: the
 * store exists on every profile that ever ran a pre-KB-3425 build, and
 * `src/pages/LogoutPage.tsx` deletes it by name. Defined ONCE, here, and
 * imported there — it was briefly declared in both files.
 */
export const FIREBASE_AUTH_IDB = "firebaseLocalStorageDb";

/**
 * Port of `raceWithBoundedTimeout` from `kidsbooked-marketing/src/lib/auth.ts:1474`
 * (KB-2500 / M20), which KB-3033's AC-3 already asked pro to take rather than
 * grow a second one.
 *
 * The contract that matters, and the reason the original exists: the timer is
 * ALWAYS cleared once the race settles. The pre-KB-2500 version armed a fresh
 * timer per call and cancelled none, so a page firing N calls held N live
 * timers. Timers are injected so a test can assert an outstanding-timer COUNT
 * rather than merely the absence of an error.
 *
 * ⚠️ Used ONLY on `/logout`, at two call sites. `signOut(auth)` races
 * `undefined`, which is not distinguishable from a real answer — tolerable
 * there only because nothing downstream acts on it destructively or renders it
 * as "done": the rescue carries on whether or not sign-out finished, and the
 * stuck screen claims nothing about it. `indexedDB.databases()` races a private
 * sentinel, because its old `[]` fallback was also a real answer (KB-3467). The deleted auth wrapper failed exactly where that
 * stops being true: its timeout values went to the Firebase SDK, which deleted
 * data on them. (`/logout`'s distinct `timed-out` outcome comes from
 * `deleteDatabase`'s own timer, not from this helper.)
 */
export function raceWithBoundedTimeout<T>(
  inner: Promise<T>,
  timeoutValue: T,
  boundMs: number,
  setTimer: (cb: () => void, ms: number) => unknown = (cb, ms) => setTimeout(cb, ms),
  clearTimer: (handle: unknown) => void = (handle) => clearTimeout(handle as never),
): Promise<T> {
  let handle: unknown = null;
  return Promise.race([
    inner,
    new Promise<T>((resolve) => {
      handle = setTimer(() => resolve(timeoutValue), boundMs);
    }),
  ]).finally(() => clearTimer(handle));
}
